import {
  CheckIcon,
  LightningBoltIcon,
  RefreshIcon,
  SparklesIcon,
  XIcon,
} from '@heroicons/react/outline';
import Image from 'next/image';
import useLocale from 'next-translate/useTranslation';
import React, { FC, useCallback, useState } from 'react';
import { toast } from 'sonner';

import clsxm from '@/lib/clsxm';

import Button from '@/components/buttons/Button';
import AspectRatioSelector from '@/components/images/AspectRatioSelector';

import axiosInstance from '@/utils/httpClient';

// Generation style presets (same as transform but for generation)
const GENERATION_PRESETS = [
  {
    id: 'professional',
    name: 'Professional',
    icon: '👔',
    description: 'Corporate headshot',
    prompt:
      'Professional corporate headshot portrait of a {gender}, wearing a tailored business suit or blazer. Clean, polished appearance with neutral studio background. Soft professional lighting, looking directly at camera with confident expression. Photorealistic, high quality, 4k.',
  },
  {
    id: 'news-anchor',
    name: 'News Anchor',
    icon: '📺',
    description: 'Broadcast ready',
    prompt:
      'Professional TV news anchor portrait of a {gender}, in a modern broadcast studio setting with professional attire. Confident, authoritative look with professional broadcast lighting. Looking at camera, photorealistic, high quality.',
  },
  {
    id: 'creator',
    name: 'Content Creator',
    icon: '🎬',
    description: 'Modern influencer',
    prompt:
      'Content creator portrait of a {gender} looking into camera, shoulder length framing, holding a small lavalier microphone near their mouth. Background: a cozy living room wall with a small gallery of framed photos. Soft daylight, neutral tones, chest-up framing, realistic texture, photorealistic.',
  },
  {
    id: 'fitness',
    name: 'Fitness Coach',
    icon: '💪',
    description: 'Athletic & healthy',
    prompt:
      'Fitness coach or wellness influencer portrait of a {gender} in athletic wear in a modern gym setting. Energetic, healthy appearance with good lighting. Confident smile, looking at camera, photorealistic, high quality.',
  },
  {
    id: 'executive',
    name: 'Executive',
    icon: '🏢',
    description: 'C-suite leader',
    prompt:
      'C-suite executive portrait of a {gender} in a premium tailored suit in a high-end executive office with city view. Commanding presence, sophisticated lighting, looking at camera with confident expression. Photorealistic, high quality.',
  },
  {
    id: 'custom',
    name: 'Custom',
    icon: '✨',
    description: 'Your vision',
    prompt: '',
  },
];

// Aspect ratio options
const ASPECT_RATIO_OPTIONS = ['9:16', '1:1', '4:5', '16:9', '4:3', '3:4'];

const ASPECT_RATIO_HINTS: Record<string, string> = {
  '9:16': 'TikTok, Reels, Shorts',
  '1:1': 'Instagram Feed',
  '4:5': 'Instagram Portrait',
  '16:9': 'YouTube, LinkedIn',
  '4:3': 'Traditional',
  '3:4': 'Portrait',
};

type Gender = 'man' | 'woman';

interface ActorPhotoGeneratorProps {
  onGenerateComplete: (photoUrl: string, aspectRatio: string) => void;
  onCancel: () => void;
}

export const ActorPhotoGenerator: FC<ActorPhotoGeneratorProps> = ({
  onGenerateComplete,
  onCancel,
}) => {
  const { t } = useLocale('ai-talking-videos');

  const [gender, setGender] = useState<Gender>('woman');
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('9:16');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedPhoto, setGeneratedPhoto] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selectedPresetData = GENERATION_PRESETS.find(
    (p) => p.id === selectedPreset,
  );

  const getGenerationPrompt = useCallback(() => {
    if (selectedPreset === 'custom') {
      return customPrompt.trim();
    }
    return selectedPresetData?.prompt.replace('{gender}', gender) || '';
  }, [selectedPreset, customPrompt, selectedPresetData, gender]);

  const canGenerate =
    selectedPreset &&
    (selectedPreset !== 'custom' || customPrompt.trim().length > 10);

  const handleGenerate = async () => {
    const prompt = getGenerationPrompt();
    if (!prompt) {
      toast.error(t('Please select a style or enter a custom prompt'));
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const response = await axiosInstance.post('/api/generate-image', {
        prompt: prompt,
        model: 'Nano Banana Pro',
        dimensions: aspectRatio,
        resolution: '2K',
        n: 1,
        waitForResult: true,
      });

      if (response.data && response.data.length > 0) {
        setGeneratedPhoto(response.data[0].image_url);
      } else {
        throw new Error('No image returned');
      }
    } catch (err: any) {
      console.error('Generation error:', err);
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        t('Failed to generate photo. Please try again.');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerate = () => {
    setGeneratedPhoto(null);
    setError(null);
  };

  const handleAccept = () => {
    if (generatedPhoto) {
      onGenerateComplete(generatedPhoto, aspectRatio);
    }
  };

  // Get aspect ratio class for preview
  const getAspectRatioClass = () => {
    switch (aspectRatio) {
      case '9:16':
        return 'aspect-[9/16]';
      case '16:9':
        return 'aspect-[16/9]';
      case '4:5':
        return 'aspect-[4/5]';
      case '4:3':
        return 'aspect-[4/3]';
      case '3:4':
        return 'aspect-[3/4]';
      default:
        return 'aspect-square';
    }
  };

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='text-center'>
        <div className='mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 shadow-lg shadow-purple-500/25'>
          <SparklesIcon className='h-6 w-6 text-white' />
        </div>
        <h3 className='text-xl font-semibold text-gray-900 dark:text-white'>
          {t('Generate AI Actor')}
        </h3>
        <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
          {t('Create a unique AI-generated actor for your videos')}
        </p>
      </div>

      {/* Generated Photo Preview */}
      {generatedPhoto ? (
        <div className='space-y-4'>
          <div className='flex justify-center'>
            <div
              className={clsxm(
                'relative overflow-hidden rounded-2xl shadow-2xl w-64',
                getAspectRatioClass(),
              )}
            >
              <Image
                src={generatedPhoto}
                alt='Generated actor'
                fill
                className='object-cover'
                sizes='256px'
                quality={95}
              />
            </div>
          </div>

          {/* Generation Info */}
          <div className='mx-auto max-w-md rounded-xl bg-gray-50 p-4 dark:bg-gray-800/50'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400'>
                <span className='text-lg'>{selectedPresetData?.icon}</span>
                <span className='font-medium'>{selectedPresetData?.name}</span>
                <span className='text-gray-400'>•</span>
                <span className='capitalize'>{gender}</span>
              </div>
              <div className='flex items-center gap-1 rounded-lg bg-gray-200 px-2 py-1 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300'>
                {aspectRatio}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className='flex flex-col gap-3 sm:flex-row sm:justify-center'>
            <Button
              variant='outline'
              onClick={handleRegenerate}
              className='order-2 sm:order-1'
            >
              <RefreshIcon className='mr-2 h-4 w-4' />
              {t('Generate Another')}
            </Button>
            <Button
              variant='dark'
              onClick={handleAccept}
              className='order-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 sm:order-2'
            >
              <CheckIcon className='mr-2 h-4 w-4' />
              {t('Use This Actor')}
            </Button>
          </div>

          <button
            onClick={onCancel}
            className='mx-auto block text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          >
            {t('Cancel')}
          </button>
        </div>
      ) : (
        <>
          {/* Gender Selector */}
          <div className='flex justify-center'>
            <div className='inline-flex rounded-xl bg-gray-100 p-1 dark:bg-gray-800'>
              <button
                onClick={() => setGender('man')}
                disabled={isGenerating}
                className={clsxm(
                  'flex items-center gap-2 rounded-lg px-6 py-2.5 text-sm font-medium transition-all',
                  gender === 'man'
                    ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white'
                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white',
                  isGenerating && 'opacity-50 cursor-not-allowed',
                )}
              >
                <span className='text-lg'>👨</span>
                {t('Male')}
              </button>
              <button
                onClick={() => setGender('woman')}
                disabled={isGenerating}
                className={clsxm(
                  'flex items-center gap-2 rounded-lg px-6 py-2.5 text-sm font-medium transition-all',
                  gender === 'woman'
                    ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white'
                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white',
                  isGenerating && 'opacity-50 cursor-not-allowed',
                )}
              >
                <span className='text-lg'>👩</span>
                {t('Female')}
              </button>
            </div>
          </div>

          {/* Aspect Ratio Selector */}
          <div className='flex items-center justify-center gap-3'>
            <span className='text-sm font-medium text-gray-600 dark:text-gray-400'>
              {t('Format')}:
            </span>
            <AspectRatioSelector
              value={aspectRatio}
              onChange={setAspectRatio}
              options={ASPECT_RATIO_OPTIONS}
              disabled={isGenerating}
            />
            {ASPECT_RATIO_HINTS[aspectRatio] && (
              <span className='text-xs text-gray-400 dark:text-gray-500'>
                {ASPECT_RATIO_HINTS[aspectRatio]}
              </span>
            )}
          </div>

          {/* Style Presets Grid */}
          <div className='grid grid-cols-2 gap-3 sm:grid-cols-3'>
            {GENERATION_PRESETS.map((preset) => {
              const isSelected = selectedPreset === preset.id;
              return (
                <button
                  key={preset.id}
                  onClick={() => setSelectedPreset(preset.id)}
                  disabled={isGenerating}
                  className={clsxm(
                    'group relative rounded-xl border-2 p-4 text-left transition-all duration-200',
                    isSelected
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50 dark:border-gray-700 dark:hover:border-purple-600 dark:hover:bg-gray-800/50',
                    isGenerating && 'opacity-50 cursor-not-allowed',
                  )}
                >
                  <div className='flex items-start gap-3'>
                    <span className='text-2xl'>{preset.icon}</span>
                    <div className='flex-1 min-w-0'>
                      <p
                        className={clsxm(
                          'font-medium transition-colors',
                          isSelected
                            ? 'text-purple-700 dark:text-purple-300'
                            : 'text-gray-900 dark:text-white',
                        )}
                      >
                        {t(preset.name)}
                      </p>
                      <p className='mt-0.5 text-xs text-gray-500 dark:text-gray-400'>
                        {t(preset.description)}
                      </p>
                    </div>
                  </div>
                  {isSelected && (
                    <div className='absolute -right-1 -top-1'>
                      <div className='flex h-5 w-5 items-center justify-center rounded-full bg-purple-500'>
                        <CheckIcon className='h-3 w-3 text-white' />
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Custom Prompt Input */}
          {selectedPreset === 'custom' && (
            <div className='animate-in fade-in slide-in-from-top-2 duration-200'>
              <label className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                {t('Describe your actor')}
              </label>
              <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder={t(
                  'e.g., "A friendly middle-aged man with glasses, wearing a casual blazer, warm smile, office background"',
                )}
                rows={3}
                maxLength={500}
                disabled={isGenerating}
                className='w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:border-purple-500 focus:ring-purple-500 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-white'
              />
              <div className='mt-1 flex items-center justify-between text-xs text-gray-400'>
                <span>
                  {t(
                    'Tip: Be specific about appearance, clothing, and setting',
                  )}
                </span>
                <span>{customPrompt.length}/500</span>
              </div>
            </div>
          )}

          {/* Loading State */}
          {isGenerating && (
            <div className='flex flex-col items-center gap-3 py-8'>
              <div className='relative h-16 w-16'>
                <div className='absolute inset-0 animate-ping rounded-full bg-purple-400 opacity-75' />
                <div className='relative flex h-16 w-16 items-center justify-center rounded-full bg-purple-500'>
                  <SparklesIcon className='h-8 w-8 text-white' />
                </div>
              </div>
              <p className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                {t('Creating your AI actor...')}
              </p>
              <p className='text-xs text-gray-400'>
                {t('This may take a few seconds')}
              </p>
            </div>
          )}

          {/* Error display */}
          {error && (
            <div className='rounded-xl bg-red-50 p-4 text-center text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400'>
              {error}
            </div>
          )}

          {/* Action buttons */}
          {!isGenerating && (
            <div className='flex flex-col gap-3 sm:flex-row sm:justify-center'>
              <Button
                variant='outline'
                onClick={onCancel}
                className='order-2 sm:order-1'
              >
                <XIcon className='mr-2 h-4 w-4' />
                {t('Cancel')}
              </Button>
              <Button
                variant='dark'
                onClick={handleGenerate}
                disabled={!canGenerate}
                className='order-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-500 sm:order-2'
              >
                <LightningBoltIcon className='mr-2 h-4 w-4' />
                {t('Generate Actor')} (2 {t('credits')})
              </Button>
            </div>
          )}

          {/* Credit info */}
          {!isGenerating && (
            <p className='text-center text-xs text-gray-400 dark:text-gray-500'>
              {t('Uses 2 image credits per generation')}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default ActorPhotoGenerator;
